import { useCallback } from 'react'
import { s, h } from '../lib/sx'
import { A } from '../lib/router'
import { config } from '../config'
import { BuildingViewer } from '../components/BuildingViewer'
import { Plate } from '../components/Plate'
import { company, heroEyebrow, heroFacts, onSiteTicker } from '../content/company'
import { copy } from '../content/copy'
import { services } from '../content/services'
import { projects } from '../content/projects'
import { news } from '../content/news'

export interface Counter {
  label: string
  shown: string
}

const featured = projects.slice(0, 3)
const latestNews = news.slice(0, 3)

export function Home({ narrow, counters }: { narrow: boolean; counters: Counter[] }) {
  /**
   * Wires the hero clip once. Kept as a ref callback — same as the prototype —
   * because the source is chosen by trying renditions in order and listening for
   * `error`, which needs the element itself rather than a React prop.
   */
  const videoRef = useCallback((el: HTMLVideoElement | null) => {
    if (!el || el.dataset.wired) return
    el.dataset.wired = '1'
    el.muted = true
    el.playsInline = true
    el.loop = true
    const chain = config.heroVideo ? [config.heroVideo] : config.heroVideoFallbacks
    let i = 0
    const load = () => {
      if (i >= chain.length) return
      el.src = chain[i]
      void el.play()?.catch(() => {})
    }
    el.addEventListener('error', () => {
      i++
      load()
    })
    load()
  }, [])

  const heroScrim = narrow
    ? 'linear-gradient(rgba(27,36,48,0.88),rgba(27,36,48,0.9))'
    : 'linear-gradient(90deg,rgba(27,36,48,0.95) 0%,rgba(27,36,48,0.86) 40%,rgba(27,36,48,0.78) 100%)'

  return (
    <div>
      <section style={s('background:#1B2430;color:#F2F4F6;position:relative;overflow:hidden;')}>
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          aria-hidden="true"
          style={s('position:absolute;inset:0;width:100%;height:100%;object-fit:cover;filter:grayscale(0.5) saturate(0.55) contrast(1.04) brightness(0.82) blur(1px);')}
        />
        <div style={s(`position:absolute;inset:0;background:${heroScrim};`)} />
        <div style={s('position:absolute;inset:0;background:linear-gradient(rgba(27,36,48,0.45) 0%,rgba(27,36,48,0.1) 22%,rgba(27,36,48,0.1) 62%,rgba(27,36,48,0.5) 100%);')} />
        <div style={s('position:absolute;inset:0;background-image:linear-gradient(rgba(255,255,255,0.045) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.045) 1px,transparent 1px);background-size:72px 72px;')} />

        <div style={s('position:relative;max-width:1320px;margin:0 auto;padding:0 clamp(18px,4vw,32px);')}>
          <div
            style={s(
              `display:flex;justify-content:space-between;gap:${narrow ? '4px' : '24px'};flex-direction:${narrow ? 'column' : 'row'};padding:16px 0;border-bottom:1px solid #2A3746;font-family:'IBM Plex Mono',monospace;font-size:10px;letter-spacing:0.2em;text-transform:uppercase;color:#5F6C7C;`,
            )}
          >
            <span>{company.legalName}</span>
            <span>{company.locations}</span>
            <span>{company.sheetRef}</span>
          </div>
        </div>

        <div style={s('max-width:1320px;margin:0 auto;padding:clamp(48px,7vw,76px) clamp(18px,4vw,32px) 0;position:relative;display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,380px),1fr));gap:clamp(32px,4vw,56px);align-items:end;')}>
          <div style={s(`padding-bottom:${narrow ? '48' : '76'}px;`)}>
            <div style={s(`display:flex;align-items:${narrow ? 'flex-start' : 'center'};gap:14px;margin-bottom:30px;`)}>
              <span style={s(`width:26px;height:3px;background:#E06A20;display:block;flex:0 0 auto;${narrow ? 'margin-top:6px;' : ''}`)} />
              <span style={s("font-family:'IBM Plex Mono',monospace;font-size:11px;letter-spacing:0.22em;text-transform:uppercase;color:#E06A20;")}>{heroEyebrow}</span>
            </div>
            <h1 style={s('font-family:Archivo,sans-serif;font-weight:800;font-size:min(clamp(52px,7.2vw,104px),12vw);line-height:0.92;letter-spacing:-0.042em;margin:0;text-wrap:balance;')}>{copy.home.headline}</h1>
            <p style={s('font-size:19px;line-height:1.6;color:#B8C0CA;max-width:600px;margin:34px 0 42px;text-wrap:pretty;')}>{copy.home.sub}</p>
            <div style={s('display:flex;gap:14px;flex-wrap:wrap;')}>
              <A href="/projects" style={s('background:#1F5C99;color:#FFFFFF;padding:17px 30px;font-size:14px;font-weight:600;white-space:nowrap;')} className={h('background:#17497A;color:#FFFFFF;')}>
                View our contracts
              </A>
              <A href="/quote" style={s('border:1px solid #4E5C6B;color:#F2F4F6;padding:17px 30px;font-size:14px;font-weight:600;white-space:nowrap;')} className={h('border-color:#F2F4F6;color:#FFFFFF;')}>
                Request a quote
              </A>
            </div>
          </div>
          <div style={s('position:relative;min-height:clamp(380px,52vh,560px);align-self:stretch;')}>
            <BuildingViewer style={s('position:absolute;left:2%;right:-10%;top:0;bottom:0;')} />
          </div>
        </div>

        <div style={s('position:relative;max-width:1320px;margin:0 auto;padding:0 clamp(18px,4vw,32px);')}>
          <div style={s('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,190px),1fr));border-top:1px solid #2A3746;')}>
            {heroFacts.map((f, i) => (
              <div key={i} style={s('padding:34px 28px 40px 0;')}>
                <div style={s('font-family:Archivo,sans-serif;font-weight:700;font-size:32px;letter-spacing:-0.025em;color:#F2F4F6;')}>{f.value}</div>
                <div style={s("font-family:'IBM Plex Mono',monospace;font-size:10.5px;letter-spacing:0.16em;text-transform:uppercase;color:#8C97A4;margin-top:10px;")}>{f.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {config.showTicker && (
        <section style={s('background:#1F5C99;color:#FFFFFF;')}>
          <div style={s("max-width:1320px;margin:0 auto;padding:22px clamp(18px,4vw,32px);display:flex;gap:40px;align-items:center;flex-wrap:wrap;font-family:'IBM Plex Mono',monospace;font-size:11.5px;letter-spacing:0.16em;text-transform:uppercase;")}>
            <span style={s('font-weight:600;')}>Currently on site</span>
            {onSiteTicker.map((t, i) => (
              <span key={i} style={s('opacity:0.85;')}>
                {t}
              </span>
            ))}
          </div>
        </section>
      )}

      <section style={s('max-width:1320px;margin:0 auto;padding:clamp(48px,7vw,110px) clamp(18px,4vw,32px);')}>
        <div style={s('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,300px),1fr));gap:clamp(36px,4vw,64px);align-items:start;')}>
          <div>
            <div style={s("font-family:'IBM Plex Mono',monospace;font-size:11px;letter-spacing:0.22em;text-transform:uppercase;color:#7E8894;")}>01 — Capability</div>
            <h2 style={s('font-family:Archivo,sans-serif;font-weight:700;font-size:clamp(34px,3.8vw,54px);line-height:1.0;letter-spacing:-0.035em;margin:18px 0 20px;')}>{copy.home.capabilityHeading}</h2>
            <p style={s('font-size:16px;line-height:1.65;color:#566070;margin:0 0 24px;')}>{copy.home.capabilityBlurb}</p>
            <A href="/services" style={s("font-family:'IBM Plex Mono',monospace;font-size:12px;letter-spacing:0.14em;text-transform:uppercase;border-bottom:2px solid #A8500F;padding-bottom:4px;")}>
              All services →
            </A>
          </div>
          <div style={s('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,260px),1fr));gap:1px;background:#DCE0E5;border:1px solid #DCE0E5;')}>
            {services.map((sv) => (
              <A key={sv.slug} href={`/services/${sv.slug}`} style={s('background:#F2F4F6;padding:34px 30px 30px;display:block;min-height:200px;')} className={h('background:#1B2430;color:#F2F4F6;')}>
                <div style={s("font-family:'IBM Plex Mono',monospace;font-size:10.5px;letter-spacing:0.18em;color:#A8500F;")}>{sv.no}</div>
                <h3 style={s('font-family:Archivo,sans-serif;font-weight:700;font-size:21px;letter-spacing:-0.01em;margin:14px 0 10px;')}>{sv.title}</h3>
                <p style={s('font-size:14px;line-height:1.6;color:#66707C;margin:0;')}>{sv.blurb}</p>
              </A>
            ))}
          </div>
        </div>
      </section>

      <section style={s('background:#1B2430;color:#F2F4F6;')}>
        <div style={s('max-width:1320px;margin:0 auto;padding:clamp(48px,7vw,100px) clamp(18px,4vw,32px);')}>
          <div style={s('display:flex;justify-content:space-between;align-items:flex-end;gap:40px;margin-bottom:48px;')}>
            <div>
              <div style={s("font-family:'IBM Plex Mono',monospace;font-size:11px;letter-spacing:0.22em;text-transform:uppercase;color:#8C97A4;")}>02 — Selected work</div>
              <h2 style={s('font-family:Archivo,sans-serif;font-weight:700;font-size:clamp(34px,3.8vw,54px);line-height:1.0;letter-spacing:-0.035em;margin:18px 0 0;')}>{copy.home.workHeading}</h2>
            </div>
            <A href="/projects" style={s("font-family:'IBM Plex Mono',monospace;font-size:12px;letter-spacing:0.14em;text-transform:uppercase;color:#F2F4F6;border-bottom:2px solid #E06A20;padding-bottom:4px;")} className={h('color:#FFFFFF;')}>
              All current contracts →
            </A>
          </div>
          <div style={s('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,250px),1fr));gap:24px;')}>
            {featured.map((p) => (
              <A key={p.slug} href={`/projects/${p.slug}`} style={s('display:block;color:#F2F4F6;')} className={h('color:#FFFFFF;')}>
                <Plate tone="dark" box="aspect-ratio:4/3;display:flex;align-items:flex-end;padding:16px;" src={p.image} caption={p.photo} captionStyle="padding:6px 10px;border-left:2px solid #E06A20;" eager />
                <div style={s("font-family:'IBM Plex Mono',monospace;font-size:10.5px;letter-spacing:0.16em;text-transform:uppercase;color:#E06A20;margin:20px 0 8px;")}>
                  {p.sector} · {p.province}
                </div>
                <h3 style={s('font-family:Archivo,sans-serif;font-weight:600;font-size:20px;line-height:1.2;letter-spacing:-0.01em;margin:0;')}>{p.title}</h3>
                <div style={s("font-family:'IBM Plex Mono',monospace;font-size:11.5px;color:#8C97A4;margin-top:10px;")}>
                  {p.value} · {p.status}
                </div>
              </A>
            ))}
          </div>
        </div>
      </section>

      <section style={s('max-width:1320px;margin:0 auto;padding:clamp(48px,7vw,110px) clamp(18px,4vw,32px);')}>
        <div style={s('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,200px),1fr));gap:1px;background:#DCE0E5;border-top:2px solid #1B2430;border-bottom:1px solid #DCE0E5;')}>
          {counters.map((c, i) => (
            <div key={i} style={s('background:#F2F4F6;padding:44px 28px;')}>
              <div style={s('font-family:Archivo,sans-serif;font-weight:800;font-size:56px;letter-spacing:-0.035em;line-height:1;')}>{c.shown}</div>
              <div style={s("font-family:'IBM Plex Mono',monospace;font-size:11px;letter-spacing:0.16em;text-transform:uppercase;color:#7E8894;margin-top:14px;")}>{c.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section style={s('background:#DCE0E5;')}>
        <div style={s('max-width:1320px;margin:0 auto;padding:clamp(48px,7vw,100px) clamp(18px,4vw,32px);display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,340px),1fr));gap:clamp(40px,5vw,80px);align-items:center;')}>
          <div>
            <div style={s("font-family:'IBM Plex Mono',monospace;font-size:11px;letter-spacing:0.22em;text-transform:uppercase;color:#66707C;")}>03 — Safety</div>
            <h2 style={s('font-family:Archivo,sans-serif;font-weight:700;font-size:clamp(34px,3.8vw,54px);line-height:1.0;letter-spacing:-0.035em;margin:18px 0 20px;')}>{copy.home.safetyHeading}</h2>
            <p style={s('font-size:16px;line-height:1.65;color:#47515D;margin:0 0 28px;max-width:520px;')}>{copy.home.safetyBlurb}</p>
            <A href="/safety" style={s("font-family:'IBM Plex Mono',monospace;font-size:12px;letter-spacing:0.14em;text-transform:uppercase;border-bottom:2px solid #A8500F;padding-bottom:4px;")}>
              Safety &amp; compliance →
            </A>
          </div>
          <Plate tone="mid" box="aspect-ratio:5/4;display:flex;align-items:flex-end;padding:18px;" src="/images/safety-site.jpg" caption="site induction, emalahleni" captionStyle="padding:6px 10px;border-left:2px solid #1F5C99;" />
        </div>
      </section>

      <section style={s('max-width:1320px;margin:0 auto;padding:clamp(48px,7vw,110px) clamp(18px,4vw,32px);')}>
        <div style={s("font-family:'IBM Plex Mono',monospace;font-size:11px;letter-spacing:0.22em;text-transform:uppercase;color:#7E8894;margin-bottom:36px;")}>04 — Latest</div>
        <div style={s('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,250px),1fr));gap:1px;background:#DCE0E5;border:1px solid #DCE0E5;')}>
          {latestNews.map((n, i) => (
            <A key={i} href="/news" style={s('background:#F2F4F6;padding:34px 30px;display:block;')} className={h('background:#1B2430;color:#F2F4F6;')}>
              <div style={s("font-family:'IBM Plex Mono',monospace;font-size:10.5px;letter-spacing:0.16em;text-transform:uppercase;color:#A8500F;")}>
                {n.date} · {n.tag}
              </div>
              <h3 style={s('font-family:Archivo,sans-serif;font-weight:600;font-size:19px;line-height:1.25;margin:16px 0 0;')}>{n.title}</h3>
            </A>
          ))}
        </div>
      </section>
    </div>
  )
}
